import { useStoreActions, useStoreState } from '@/stores/store';
import { useNavigation } from '@react-navigation/native';
import {
  StackNavigationProp,
  createStackNavigator,
} from '@react-navigation/stack';
import { getHeaderTitle } from '@react-navigation/elements';
import { useEffect } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import { AnimatedHeader } from '@/components/ui/therapist/AnimatedHeader';
import { CheckPassword } from '@/components/ReactNativeGesturePassword/example/CheckPassword';
import { TemplateMode } from '@/components/Template/types';
import { Icon } from '@/components/Icon';
import { SetPassword } from '@/components/ReactNativeGesturePassword/example/SetPassword';
import { SidebarAnimationProvider } from '@/components/ui/therapist/CollapsibleSidebar/SidebarAnimationContext';
import { AboutNeolexon } from '@/screens/TherapistSettings/AboutNeolexon/';
import { Account } from '@/screens/TherapistSettings/Account';
import AddPatient from '@/screens/AddPatient';
import { Aufgaben } from '@/screens/Aufgaben';
import { AufgabenExercise } from '@/screens/AufgabenExercise';
import { AufgabenExerciseText } from '@/screens/AufgabenExercise/text';
import { AufgabenSettings } from '@/screens/AufgabenSettings';
import { BildKarten } from '@/screens/BildKarten';
import { BildKartenExercise } from '@/screens/BildKartenExercise';
import { Ergebnisse } from '@/screens/Ergebnisse';
import { Consent } from '@/screens/TherapistSettings/Consent';
import { Contact } from '@/screens/TherapistSettings/Contact';
import { DashboardContextProvider } from '@/contexts/DashboardContext';
import { Filler } from '@/screens/Filler';
import { HomeTherapist } from '@/screens/HomeTherapist/index';
import { LockScreen } from '@/screens/TherapistSettings/LockScreen/';
import { MarketingChannelQuestionnaire } from '@/screens/MarketingChannelQuestionnaire/';
import { PasswordChangeScreen } from '@/screens/PasswordChangeScreen';
import { PasswordUnlock } from '@/screens/PasswordUnlock';
import { PatientProfile } from '@/screens/PatientProfile';
import ResultDetail from '@/screens/ResultDetail';
import { Results } from '@/screens/Results';
import { SelfTraining } from '@/screens/SelfTraining';
import SelfTrainingSettings from '@/screens/SelfTrainingSettings';
import SentenceSet from '@/screens/SentenceSet';
import { LockScreenSettings } from '@/screens/LockScreenSettings';
import { TemplateList } from '@/screens/TemplateList';
import { Template } from '@/components/Template/types';
import TextSet from '@/screens/TextSet';
import { TherapistSettings } from '@/screens/TherapistSettings';
import Wordset from '@/screens/WordSet/index';
import { TemplateAssign } from '@/screens/TemplateAssign';
import { TemplatesSelect } from '@/screens/TemplatesSelect';
import { TherapistDemoProvider } from '@/contexts/TherapistDemoContext';

import { appTheme } from '@/config/colors';
import { useMarketingChannelRedirect } from '@/hooks/useMarketingChannelRedirect';
import { Patient, Set } from '@/stores/store.types';
import { logInfo } from '@/utils/logHandling';
import { DownloadProvider } from '@/contexts/DownloadAssetContext/DownloadContext';
import { IS_IOS } from '@/utils/platform';

type SetParams = {
  setId?: string;
  selectedID?: string;
  apiName: string;
  templateMode?: TemplateMode;
  fromTemplate?: string;
};

export type TherapistStackParamList = {
  Home: undefined;
  CheckPassword: { blockBackFromSelfTraining?: boolean };
  AddPatientScreen?: { _id?: string };
  PatientProfile: { id: string; patients?: Patient[]; replacedTitle?: string };
  SelfTrainingSettings: {
    id: string;
  };
  Einwilligungen: undefined;
  Sperrbildschirm: {
    setPatterLockFailed: boolean;
  };
  PasswordChangeScreen: undefined;
  ResultsScreen: undefined;
  ResultDetailScreen: {
    exerciseSetName?: string;
    exerciseClass?: string;
  };
  EinstellungenPatient: undefined;
  WordSet: SetParams;
  SentenceSet: SetParams;
  TextSet: { setId?: string; selectedID?: string; apiName?: string };
  BildKarten: undefined;
  BildKartenExercise: undefined;
  Aufgaben: {
    exerciseClass: string;
    patientId: string;
    setId: string;
    isNetworkError?: string;
    exerciseSettings: Record<string, unknown>;
    isSelf: boolean;
    selectedPatient?: Patient;
    activeSets?: unknown;
    preview?: boolean;
  };
  AufgabenSettings: {
    setId: string;
    setType: string;
    exerciseSettings?: Set;
  };
  AufgabenExercise: undefined;
  AufgabenExerciseText: undefined;
  SetPassword: { mode?: string };
  PasswordUnlock: { fromRoute: 'SetPassword' | 'other' };
  Ergebnisse: undefined;
  SelfTraining: {
    selectedPatient: Patient;
    activeSets: string[];
    preview?: boolean;
  };
  TherapistSettings: undefined;
  AboutNeolexon: undefined;
  Privacy: undefined;
  Account: undefined;
  LockScreen: undefined;
  MarketingChannelQuestionnaire: undefined;
  Consent: undefined;
  Contact: undefined;
  TemplateList: undefined;
  TemplateSelect: { type: string };
  TemplateAssign: { template: Template };
};

const Stack = createStackNavigator<TherapistStackParamList>();

// Stack Navigator
function TherapistNavigationStack() {
  useMarketingChannelRedirect();

  return (
    <Stack.Navigator
      initialRouteName="Home"
      screenOptions={{
        header: ({ navigation, route, options }) => {
          const { canGoBack, goBack } = navigation;
          const onBack = canGoBack() ? () => goBack() : undefined;
          const title = getHeaderTitle(options, route.name);
          const replacedTitle = (
            route.params as { replacedTitle?: string } | undefined
          )?.replacedTitle;

          return (
            <AnimatedHeader
              onBack={onBack}
              title={title}
              name={route.name}
              options={options}
              replacedTitle={replacedTitle}
            />
          );
        },
        headerMode: 'float',
        animationEnabled: false,
      }}
    >
      <Stack.Screen
        name="Home"
        component={HomeTherapist}
        options={{
          title: 'Patient:innen',
          headerTitleAllowFontScaling: false,
        }}
      />
      <Stack.Screen
        name="TemplateList"
        component={TemplateList}
        options={{
          title: 'Bibliothek',
          headerTitleAllowFontScaling: false,
        }}
      />
      <Stack.Screen
        name="LockScreen"
        component={LockScreen}
        options={{
          title: 'Sperrbildschirm',
          headerTitleAllowFontScaling: false,
        }}
      />
      <Stack.Screen
        name="AboutNeolexon"
        options={{
          title: 'Über die App',
          headerTitleAllowFontScaling: false,
        }}
        component={AboutNeolexon}
      />
      <Stack.Screen
        name="MarketingChannelQuestionnaire"
        options={{
          title: '',
          headerTitleAllowFontScaling: false,
        }}
        component={MarketingChannelQuestionnaire}
      />
      <Stack.Screen
        name="Account"
        options={{
          title: 'Benutzerkonto',
          headerTitleAllowFontScaling: false,
        }}
        component={Account}
      />
      <Stack.Screen
        name="Consent"
        options={{ title: 'Meine Einwilligungen' }}
        component={Consent}
      />
      <Stack.Screen
        name="TemplateAssign"
        options={{
          title: 'Vorlage zuweisen',
          headerTitleAllowFontScaling: false,
        }}
        component={TemplateAssign}
      />
      <Stack.Screen
        name="TemplateSelect"
        options={{
          title: 'Vorlage aus Bibliothek wählen',
          headerTitleAllowFontScaling: false,
        }}
        component={TemplatesSelect}
      />
      <Stack.Screen
        name="Contact"
        options={{ title: 'Kontakt' }}
        component={Contact}
      />
      <Stack.Screen
        name="PatientProfile"
        component={PatientProfile}
        options={{
          title: 'Patienten-Profil',
          headerTitleAllowFontScaling: false,
        }}
      />
      <Stack.Screen
        name="Privacy"
        component={Filler}
        options={{
          headerShown: false,
          presentation: 'modal',
        }}
      />
      <Stack.Screen
        name="AddPatientScreen"
        component={AddPatient}
        options={{
          title: 'Add Patient',
          headerTitleAllowFontScaling: false,
        }}
      />
      <Stack.Screen
        name="SelfTrainingSettings"
        component={SelfTrainingSettings}
        options={{
          title: 'Eigentraining einstellen',
          headerTitleAllowFontScaling: false,
        }}
      />

      <Stack.Screen
        name="Sperrbildschirm"
        component={LockScreenSettings}
        options={{
          title: 'Sperrbildschirm',
          headerTitleAllowFontScaling: false,
        }}
      />
      <Stack.Screen
        name="PasswordChangeScreen"
        component={PasswordChangeScreen}
        options={{
          title: 'Passwort ändern',
          headerTitleAllowFontScaling: false,
        }}
      />
      <Stack.Screen
        name="ResultsScreen"
        component={Results}
        options={{
          headerTitleAllowFontScaling: false,
          title: 'Ergebnisse',
        }}
      />
      <Stack.Screen
        name="ResultDetailScreen"
        component={ResultDetail}
        options={{
          title: 'Ergebnisdetails',
          headerTitleAllowFontScaling: false,
        }}
      />
      <Stack.Screen
        name="WordSet"
        component={Wordset}
        options={{
          title: 'Wörterset bearbeiten',
          headerTitleAllowFontScaling: false,
        }}
      />
      <Stack.Screen
        name="SentenceSet"
        component={SentenceSet}
        options={{
          title: 'Sätzeset bearbeiten',
          headerTitleAllowFontScaling: false,
        }}
      />
      <Stack.Screen
        name="TextSet"
        component={TextSet}
        options={{
          title: 'Textset bearbeiten',
          headerTitleAllowFontScaling: false,
        }}
      />
      <Stack.Screen
        name="CheckPassword"
        component={CheckPassword}
        options={{
          headerShown: false,
          animationEnabled: false,
        }}
      />
      <Stack.Screen
        name="SetPassword"
        component={SetPassword}
        options={{
          headerShown: false,
          animationEnabled: false,
        }}
      />
      <Stack.Screen
        name="PasswordUnlock"
        component={PasswordUnlock}
        options={{
          title: 'PasswordUnlock',
          headerShown: false,
        }}
      />

      <Stack.Screen
        name="Ergebnisse"
        component={Ergebnisse}
        options={{
          title: 'Ergebnisse',
          headerTitleAllowFontScaling: false,
        }}
      />
      <Stack.Screen
        name="TherapistSettings"
        component={TherapistSettings}
        options={{
          title: 'Einstellungen',
          headerTitleAllowFontScaling: false,
        }}
      />
      {/* Headers are adjusted within the views
      They are maginified for the patients to easily read them */}
      <Stack.Group
        screenOptions={{
          headerStyle: {
            height: 64,
            backgroundColor: appTheme.primary,
          },
          headerBackTitleStyle: {
            fontSize: 24,
          },
          headerTitleStyle: {
            fontSize: 24,
          },
          headerBackImage: (x) =>
            IS_IOS ? (
              <Icon name="chevronLeft" color={x.tintColor} size={38} />
            ) : (
              <Icon name="arrowLeft" color={x.tintColor} size={38} />
            ),
        }}
      >
        <Stack.Screen
          name="BildKarten"
          component={BildKarten}
          options={{
            headerTitle: 'Bilder Anzahl',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="SelfTraining"
          component={SelfTraining}
          options={{
            title: 'Eigentraining',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="Aufgaben"
          component={Aufgaben}
          options={{
            title: 'Aufgaben auswählen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="AufgabenSettings"
          component={AufgabenSettings}
          options={{
            title: 'Aufgabeneinstellungen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="AufgabenExercise"
          component={AufgabenExercise}
          options={{
            title: '',
          }}
        />
        <Stack.Screen
          name="AufgabenExerciseText"
          component={AufgabenExerciseText}
          options={{
            title: '',
          }}
        />

        <Stack.Screen
          name="BildKartenExercise"
          component={BildKartenExercise}
        />
      </Stack.Group>
    </Stack.Navigator>
  );
}

// This stack will be the mounted one when the user is authorized as a patient
export function TherapistStack() {
  const { addListener, navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();

  const {
    appStartResumeLock,
    appLockPattern,
    eigentrainingLock,
    lastVisitedPage,
  } = useStoreState((state) => ({
    appStartResumeLock: state.appStartResumeLock,
    appLockPattern: state.appLockPattern,
    eigentrainingLock: state.eigentrainingLock,
    lastVisitedPage: state.lastVisitedPage,
  }));

  const { changeLastVisitedPage } = useStoreActions((actions) => ({
    changeLastVisitedPage: actions.changeLastVisitedPage,
  }));

  useEffect(() => {
    const unsubscribe = addListener('state', (e) => {
      const currentRoute = e.data.state?.routes[e.data.state?.index];
      /**
       * We keep the last visited page
       * to show the lockscreen (CheckPassword) when the app is refreshed while being in Selftraining and that the option 'Sperrbildschirm
       * - Nach Eigentraining (Klinik-Version)' is on.
       * The Privacy screen or the Password forgotten screen interfer with this behaviour, so we exclude them from being set as last visited page
       */
      if (
        currentRoute &&
        currentRoute.name !== 'Privacy' &&
        currentRoute.name !== 'PasswordUnlock'
      ) {
        changeLastVisitedPage(currentRoute);
      }
    });
    return unsubscribe;
  }, [addListener]);

  useEffect(() => {
    // Check on initial mount (handles app restart from killed state)
    const comesFromSelftraining =
      lastVisitedPage &&
      (lastVisitedPage.name === 'SelfTraining' ||
        (['Aufgaben', 'AufgabenExercise'].includes(lastVisitedPage.name) &&
          lastVisitedPage.params?.isSelf) ||
        (lastVisitedPage.name === 'CheckPassword' &&
          lastVisitedPage.params?.blockBackFromSelfTraining));

    logInfo('Initializing TherapistStack', {
      lastVisitedPageName: lastVisitedPage?.name,
      lastVisitedPageParams: lastVisitedPage?.params,
      eigentrainingLock,
      appStartResumeLock,
      comesFromSelftraining,
    });

    if (appStartResumeLock && appLockPattern) {
      // On Web, this means that the user is coming from the login page
      // As the Stack are different, the lastVisistedPage has no value
      if (
        lastVisitedPage === undefined ||
        !Object.keys(lastVisitedPage || {}).length
      ) {
        return;
      }
      navigate('CheckPassword', {});
    } else if (
      lastVisitedPage &&
      appLockPattern &&
      eigentrainingLock &&
      comesFromSelftraining
    ) {
      navigate('CheckPassword', { blockBackFromSelfTraining: true });
    }
  }, []);

  useEffect(() => {
    function handleAppStateChange(nextState: AppStateStatus) {
      logInfo('AppState changed', {
        nextState,
        appStartResumeLock,
        lastVisitedPage: lastVisitedPage?.name,
        eigentrainingLock,
      });
      if (nextState === 'active' && appStartResumeLock && appLockPattern) {
        // When app becomes active (comes from background), show password screen
        navigate('CheckPassword', {});
      }
    }

    const appstateChangeEventListener = AppState.addEventListener(
      'change',
      handleAppStateChange,
    );

    return () => {
      appstateChangeEventListener.remove();
    };
  }, [appStartResumeLock, appLockPattern, lastVisitedPage]);

  return (
    <DownloadProvider>
      <DashboardContextProvider>
        <SidebarAnimationProvider>
          <TherapistDemoProvider>
            <TherapistNavigationStack />
          </TherapistDemoProvider>
        </SidebarAnimationProvider>
      </DashboardContextProvider>
    </DownloadProvider>
  );
}
